// Azioni sugli allegati: download e stampa, sempre asincrone e senza window.open/redirect
import { toast } from "sonner";
import { carica } from "./attachCache";
import { apriPdf } from "./pdfjs";

const IMG = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif"];
const TXT = ["txt", "csv", "log", "json", "xml", "md", "ini", "cfg", "conf", "yml", "yaml", "js", "py", "css", "sql", "eml", "ics", "vcf"];

function esc(s) {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
}

// Download con object URL + <a download> temporaneo (non bloccato dai browser)
export async function scaricaBlob(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome || "allegato";
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}

export async function scaricaAllegatoRemoto(emailId, allegato) {
  const t = toast.loading(`Preparo ${allegato.nome}...`);
  try {
    const { blob } = await carica(emailId, allegato.part);
    await scaricaBlob(blob, allegato.nome);
    toast.success("Download avviato: " + allegato.nome, { id: t });
  } catch (e) {
    toast.error(`Download non riuscito: ${e?.message || "errore"}`, { id: t });
  }
}

// PDF → immagini delle pagine (pdf.js) per una stampa identica su ogni browser
async function paginePdf(blob) {
  const doc = await apriPdf(await blob.arrayBuffer());
  const out = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const vp = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
    out.push(canvas.toDataURL("image/jpeg", 0.9));
  }
  return out;
}

async function corpoStampa(blob, tipo) {
  const t = (tipo || "").toLowerCase();
  if (t === "pdf") return (await paginePdf(blob)).map((src) => `<img src="${src}" class="pag">`).join("");
  if (IMG.includes(t)) return `<img src="${URL.createObjectURL(blob)}" style="max-width:100%;max-height:100vh">`;
  if (TXT.includes(t)) return `<pre>${esc(await blob.text())}</pre>`;
  if (["html", "htm"].includes(t)) return await blob.text();
  if (["docx", "docm"].includes(t)) {
    const mammoth = await import("mammoth/mammoth.browser");
    return (await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() })).value;
  }
  if (["xlsx", "xls", "xlsm", "ods"].includes(t)) {
    const XLSX = await import("xlsx");
    const wb = XLSX.read(await blob.arrayBuffer(), { type: "array" });
    return wb.SheetNames.map((n) => `<h3>${esc(n)}</h3>${XLSX.utils.sheet_to_html(wb.Sheets[n])}`).join("");
  }
  return null;
}

// Stampa tramite iframe nascosto + contentWindow.print() (mai window.print() sulla pagina principale)
export async function stampaAllegato(emailId, allegato) {
  const t = toast.loading(`Preparo la stampa di ${allegato.nome}...`);
  try {
    const { blob } = await carica(emailId, allegato.part);
    const corpo = await corpoStampa(blob, allegato.tipo);
    if (corpo === null) {
      toast.error("Stampa non disponibile per questo formato: scaricalo e stampalo dall'app dedicata", { id: t });
      return;
    }
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(allegato.nome)}</title>
<style>body{margin:0;font-family:Arial,sans-serif;color:#111}pre{white-space:pre-wrap;word-break:break-word;font:13px/1.5 monospace;padding:16px}
img.pag{display:block;width:100%;page-break-after:always}img.pag:last-child{page-break-after:auto}table{border-collapse:collapse}td,th{border:1px solid #999;padding:3px 6px;font-size:12px}
@page{margin:10mm}</style></head><body>${corpo}</body></html>`;
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    frame.setAttribute("aria-hidden", "true");
    document.body.appendChild(frame);
    await new Promise((ok) => {
      frame.onload = () => ok();
      frame.srcdoc = html;
    });
    const doc = frame.contentDocument;
    await Promise.all([...doc.images].map((im) => (im.complete ? null : new Promise((ok) => { im.onload = im.onerror = ok; }))));
    toast.dismiss(t);
    const win = frame.contentWindow;
    const pulisci = () => setTimeout(() => frame.remove(), 500);
    win.addEventListener("afterprint", pulisci, { once: true });
    setTimeout(() => frame.isConnected && frame.remove(), 120_000);
    win.focus();
    win.print();
  } catch (e) {
    toast.error(`Stampa non riuscita: ${e?.message || "errore"}`, { id: t });
  }
}
